import { useCallback, useState } from 'react';
import {
  Box, Typography, Stack, Chip, Button, Divider, LinearProgress,
  IconButton, Tooltip,
} from '@mui/material';
import { ArrowForwardOutlined, RefreshOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AttendanceOverviewResponse } from '../../../api/attendance';
import OrgAttendanceKpis from '../../../components/attendance/OrgAttendanceKpis';
import SetupGuide from '../../../components/onboarding/SetupGuide';
import TodayExceptionsPanel from './TodayExceptionsPanel';
import OverviewCard, { OverviewEmptyState } from './OverviewCard';

const humanizeCode = (value: string): string =>
  value.replace(/_/g, ' ').replace(/^\w/, (char) => char.toUpperCase());

const AttendanceOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // OrgAttendanceKpis owns the single /overview fetch. onData shares the exact
  // payload with the cards below so counts cannot drift between duplicate calls.
  const [data, setData] = useState<AttendanceOverviewResponse | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    setData(null);
    setReloadKey((key) => key + 1);
  }, []);

  const pending = data?.pending_approvals ?? [];
  const pendingTotal = data?.kpis.pending_approvals ?? pending.length;
  const departments = data?.department_summary ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('attendance:overview.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attendance:overview.subtitle')}
          </Typography>
        </Box>
        <Tooltip title={t('common:actions.refresh')}>
          <IconButton
            size="small"
            onClick={refresh}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              '&:hover': { borderColor: 'text.disabled', bgcolor: 'background.paper' },
            }}
          >
            <RefreshOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <SetupGuide section="attendance" />

      <Box sx={{ width: '100%', mb: 2 }}>
        <OrgAttendanceKpis key={reloadKey} variant="full" onData={setData} />
      </Box>

      <TodayExceptionsPanel key={`tx-${reloadKey}`} />

      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(320px, 5fr)' },
          alignItems: 'stretch',
          gap: 1.5,
        }}
      >
        <OverviewCard
          title={t('attendance:overview.cards.pendingApprovals')}
          subtitle={t('attendance:overview.cards.pendingApprovalsSubtitle')}
          minHeight={330}
          contentSx={{ display: 'flex', flexDirection: 'column' }}
          action={(
            <Button
              size="small"
              endIcon={<ArrowForwardOutlined sx={{ fontSize: 14 }} />}
              onClick={() => navigate('/attendance/approvals')}
              sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: 1, whiteSpace: 'nowrap' }}
            >
              {t('attendance:overview.viewAll')}
            </Button>
          )}
        >
          {pending.length === 0 ? (
            <OverviewEmptyState
              title={t('attendance:overview.noPendingApprovals')}
              hint={t('attendance:overview.noPendingApprovalsHint')}
            />
          ) : (
            <>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.5 }}>
                <Stack divider={<Divider flexItem />}>
                  {pending.map((approval) => {
                    const typeLabel = t(`attendance:approvals.typeLabels.${approval.type}`, {
                      defaultValue: humanizeCode(approval.type),
                    });
                    const urgent = approval.priority === 'high';
                    return (
                      <Box
                        key={approval.id}
                        sx={{
                          minHeight: 58,
                          display: 'grid',
                          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                          alignItems: 'center',
                          gap: 1.25,
                          py: 0.75,
                        }}
                      >
                        <Chip
                          label={typeLabel}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            maxWidth: 124,
                            borderRadius: 1,
                            borderColor: urgent ? 'warning.main' : 'divider',
                            color: urgent ? 'warning.dark' : 'text.secondary',
                            fontSize: '0.65rem',
                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }} noWrap title={approval.worker?.name}>
                            {approval.worker?.name}
                          </Typography>
                          {approval.detail ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }} noWrap title={approval.detail}>
                              {approval.detail}
                            </Typography>
                          ) : null}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ color: urgent ? 'warning.dark' : 'text.disabled', fontWeight: urgent ? 700 : 500, whiteSpace: 'nowrap' }}
                        >
                          {t('attendance:overview.daysOld', { count: approval.age })}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
              {pendingTotal > pending.length ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 'auto', pt: 1 }}>
                  {t('attendance:overview.showingLatest', { shown: pending.length, total: pendingTotal })}
                </Typography>
              ) : null}
            </>
          )}
        </OverviewCard>

        <OverviewCard
          title={t('attendance:overview.cards.departmentSummary')}
          subtitle={t('attendance:overview.cards.departmentSummarySubtitle')}
          minHeight={330}
          contentSx={{ display: 'flex', flexDirection: 'column' }}
        >
          {departments.length === 0 ? (
            <OverviewEmptyState
              title={t('attendance:overview.noDepartmentData')}
              hint={t('attendance:overview.noDepartmentDataHint')}
            />
          ) : (
            <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.5 }}>
              <Stack divider={<Divider flexItem />}>
                {departments.map((department) => {
                  const percent = department.total > 0
                    ? Math.min(100, Math.round((department.present / department.total) * 100))
                    : 0;
                  return (
                    <Box key={department.department} sx={{ minHeight: 72, py: 0.9 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap title={department.department}>
                          {department.department}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          <Box component="span" sx={{ fontWeight: 700, color: 'success.main' }}>{department.present}/{department.total}</Box>
                          {' '}{t('attendance:overview.tableHeaders.present').toLowerCase()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percent}
                        sx={{
                          height: 4,
                          my: 0.75,
                          borderRadius: 2,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { bgcolor: 'success.main' },
                        }}
                      />
                      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="error.main" sx={{ fontSize: '0.66rem' }}>
                          <strong>{department.absent}</strong> {t('attendance:overview.tableHeaders.absent')}
                        </Typography>
                        <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.66rem' }}>
                          <strong>{department.late}</strong> {t('attendance:overview.tableHeaders.late')}
                        </Typography>
                        <Typography variant="caption" color="info.main" sx={{ fontSize: '0.66rem' }}>
                          <strong>{department.on_leave}</strong> {t('attendance:overview.tableHeaders.leave')}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </OverviewCard>
      </Box>
    </Box>
  );
};

export default AttendanceOverview;
